import { Head, Link, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button, buttonVariants } from "../../components/ui/button"
import { cn } from "../../lib/cn"

type PaymentTermItem = {
  id: number
  name: string
  days: string
  active: boolean
  invoices_count: number
}

type Props = {
  payment_terms: PaymentTermItem[]
}

export default function DatasPagamentoIndex({ payment_terms }: Props) {
  function handleDelete(id: number, name: string) {
    if (!confirm(`Deseja excluir a condição "${name}"?`)) return
    router.delete(`/datas-pagamento/${id}`)
  }

  return (
    <>
      <Head title="Datas de Pagamento" />
      <AppLayout>
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-white">Datas de Pagamento</h1>
            <p className="text-slate-400">
              Gerencie as condições de pagamento (à vista, 30 dias, 60 dias, etc.) usadas nas notas
              fiscais.
            </p>
          </div>

          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-slate-400">{payment_terms.length} condição(ões)</p>
            <Link href="/datas-pagamento/nova" className={buttonVariants()}>
              <Plus className="mr-2 h-4 w-4" />
              Nova condição
            </Link>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Dias</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-center font-medium">Notas vinculadas</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {payment_terms.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-400" colSpan={5}>
                      Nenhuma condição de pagamento cadastrada.
                    </td>
                  </tr>
                ) : (
                  payment_terms.map((term) => (
                    <tr key={term.id} className="border-b border-slate-800/80 last:border-b-0">
                      <td className="px-4 py-3 font-medium text-slate-100">{term.name}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-300">
                        {term.days} {Number(term.days) === 1 ? "dia" : "dias"}
                      </td>
                      <td className="px-4 py-3">
                        {term.active ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-950/50 px-2 py-0.5 text-xs font-medium text-emerald-400">
                            Ativa
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-400">
                            Inativa
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-200">{term.invoices_count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            href={`/datas-pagamento/${term.id}/editar`}
                            className={cn(
                              buttonVariants({ variant: "outline" }),
                              "border-slate-700 bg-transparent text-xs"
                            )}
                          >
                            Editar
                          </Link>
                          <Button
                            variant="outline"
                            className="border-rose-800/60 bg-transparent text-xs text-rose-400 hover:bg-rose-950/30"
                            onClick={() => handleDelete(term.id, term.name)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AppLayout>
    </>
  )
}
